import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Trophy } from 'lucide-react';

import { env } from '@/env';
import {
  useGetMyLeaguesQuery,
  useGetTierRankingQuery,
  useGetContextualRankingQuery,
} from '@/services/qbankApi';
import { useResolveProfilesBySubsQuery } from '@/services/socialApi';
import { getMyLogtoSub } from '@/lib/logto';
import type { LeagueCentre, LeagueRival } from '@/types/qbank';
import { Loader } from '@/components/common/Loader';
import { cn } from '@/lib/cn';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { formatNumero } from '@/lib/fmt';

/**
 * League standings — the caller's current tier ranking (QBank
 * `GET /leagues/{id}/tiers/{tierId}/ranking`). Player rows carry only `sub`, so
 * names + avatars are resolved via QSocials `resolveProfilesBySubs` (falls back
 * to a placeholder until that endpoint ships / for unresolved subs).
 *
 * Reached from the Hub LeagueCard. Self-explains when there's no active league,
 * the league hasn't started, or the user isn't entitled.
 */
export function League() {
  const { t } = useTranslation('general');
  const lingua = useLinguaFormato();

  const myQ = useGetMyLeaguesQuery(undefined, { skip: !env.featureQbankEnabled });
  const enrollment = myQ.data?.leagues?.[0];
  const leagueId = enrollment?.league.leagueId;
  const tierId = enrollment?.tier.tierId;

  const rankingQ = useGetTierRankingQuery(
    { leagueId: leagueId ?? '', tierId: tierId ?? '', limit: 50 },
    { skip: !leagueId || !tierId },
  );
  const players = useMemo(() => rankingQ.data?.players ?? [], [rankingQ.data]);
  const myRank = rankingQ.data?.myRank ?? null;

  // The "chase" strip: the handful of players around ME, cross-tier.
  //
  // This is the difference between a table and a game. A top-50 tells a player
  // ranked #430 nothing they can act on — they can't even find themselves.
  // `distanceToRank1` is vanity; the gap to the player one rank up is a plan for
  // tonight. QBank already computes it (contextual mode), we just never asked.
  const mySub = getMyLogtoSub();
  const chaseQ = useGetContextualRankingQuery(
    { leagueId: leagueId ?? '', sub: mySub ?? '', radius: 3 },
    { skip: !leagueId || !mySub },
  );
  // Every error here means "nothing to show yet" (409 not started, 409 booting,
  // 404 not enrolled before the first credit), never "broken" — so the strip
  // self-hides rather than shouting at a player who did nothing wrong.
  const chase = chaseQ.isError ? null : (chaseQ.data ?? null);

  // Resolve player subs → names/avatars (sorted for a stable cache key).
  const subs = useMemo(() => {
    const all = [
      ...players.map((p) => p.sub),
      ...(chase?.rivals ?? []).map((r) => r.sub),
    ];
    return [...new Set(all)].sort();
  }, [players, chase]);
  const profilesQ = useResolveProfilesBySubsQuery({ subs }, { skip: subs.length === 0 });
  const profileBySub = useMemo(() => {
    const m = new Map<string, { displayName?: string | null; avatarUrl?: string | null }>();
    for (const r of profilesQ.data ?? []) m.set(r.sub, r);
    return m;
  }, [profilesQ.data]);

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto px-4 pb-6 pt-4">
      <header className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/15 text-brand">
          <Trophy aria-hidden className="h-5 w-5" strokeWidth={2.5} />
        </span>
        <div className="flex min-w-0 flex-col">
          <h1 className="truncate text-lg font-bold text-fg">
            {enrollment?.league.leagueName ?? t('engagement.league', { defaultValue: 'League' })}
          </h1>
          {enrollment && (
            <p className="flex items-center gap-2 text-xs text-fg-muted">
              <span className="rounded-md bg-brand/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand">
                {enrollment.tier.tierName}
              </span>
              <span>
                #{enrollment.rank} / {enrollment.tier.playerCount} ·{' '}
                <span className="tabular-nums">{formatNumero(Math.round(enrollment.score), lingua)}</span> Elo
              </span>
            </p>
          )}
        </div>
      </header>

      {!env.featureQbankEnabled ? (
        <Empty text={t('engagement.league_unavailable', { defaultValue: 'League not available' })} />
      ) : myQ.isLoading ? (
        <Loader />
      ) : !enrollment ? (
        <Empty text={t('engagement.no_active_league', { defaultValue: 'No active league yet' })} />
      ) : rankingQ.isLoading ? (
        <Loader />
      ) : rankingQ.isError || players.length === 0 ? (
        <Empty
          text={t('engagement.league_not_started', {
            defaultValue: 'Standings will appear once the league starts',
          })}
        />
      ) : (
        <>
          {chase && chase.rivals.length > 0 && (
            <ChaseStrip
              centre={chase.center}
              rivals={chase.rivals}
              nameFor={(sub, rank) =>
                profileBySub.get(sub)?.displayName ||
                `${t('engagement.player', { defaultValue: 'Player' })} ${rank ?? ''}`.trim()
              }
              t={t}
            />
          )}
        <ul className="flex flex-col gap-1.5">
          {players.map((p) => {
            const prof = profileBySub.get(p.sub);
            const name = prof?.displayName || `${t('engagement.player', { defaultValue: 'Player' })} ${p.rank}`;
            const isMe = myRank != null && p.rank === myRank;
            return (
              <li
                key={p.sub}
                className={cn(
                  'flex items-center gap-3 rounded-xl p-2.5',
                  isMe ? 'bg-brand/15 ring-1 ring-brand/40' : 'bg-bg-secondary',
                )}
              >
                <span className="w-7 shrink-0 text-center text-sm font-bold tabular-nums text-fg-muted">
                  {p.rank}
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-tertiary text-xs font-bold text-fg">
                  {prof?.avatarUrl ? (
                    <img src={prof.avatarUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    (name.charAt(0) || '?').toLocaleUpperCase(lingua)
                  )}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
                  {name}
                  {isMe && (
                    <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-brand">
                      {t('engagement.you', { defaultValue: 'You' })}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-sm font-bold tabular-nums text-fg">
                  {formatNumero(Math.round(p.score), lingua)}
                </span>
              </li>
            );
          })}
        </ul>
        </>
      )}
    </div>
  );
}

/**
 * "Who's next" — the ±N window around the player, cross-tier.
 *
 * Shows the gap to the player directly above rather than to rank 1: the point is
 * to hand over a target you can actually reach tonight. Rivals arrive sorted by
 * `position` (negative = above me), and the centre is excluded from `rivals`, so
 * we splice it back in to keep the ladder readable as one column.
 */
function ChaseStrip({
  centre,
  rivals,
  nameFor,
  t,
}: {
  centre: LeagueCentre;
  rivals: LeagueRival[];
  nameFor: (sub: string, rank: number | null) => string;
  t: TFunction;
}) {
  const lingua = useLinguaFormato();
  const rows = useMemo(() => {
    const merged = [
      ...rivals.map((r) => ({ ...r, isMe: false })),
      { ...centre, deltaScore: 0, position: 0, isMe: true },
    ];
    // By rank when we have it; `position` is the fallback the server gives us.
    return merged.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0) || a.position - b.position);
  }, [centre, rivals]);

  // The player one rank up — the only number that turns into an action.
  const ahead = rivals.filter((r) => r.position < 0).sort((a, b) => b.position - a.position)[0];
  const gap = ahead ? Math.max(0, Math.round(ahead.score - centre.score)) : null;

  return (
    <section className="flex flex-col gap-2 rounded-2xl border border-brand/30 bg-brand/5 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-[11px] font-bold uppercase tracking-wide text-brand">
          {t('engagement.chase_title', { defaultValue: 'Who you can catch' })}
        </h2>
        {gap != null && ahead && (
          <p className="min-w-0 truncate text-xs text-fg-muted">
            {t('engagement.chase_gap', {
              defaultValue: '+{{gap}} to pass {{name}}',
              gap: formatNumero(gap, lingua),
              name: nameFor(ahead.sub, ahead.rank),
            })}
          </p>
        )}
      </div>

      <ul className="flex flex-col gap-1">
        {rows.map((r) => (
          <li
            key={r.sub}
            className={cn(
              'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm',
              r.isMe ? 'bg-brand/20 font-bold text-fg' : 'text-fg-muted',
            )}
          >
            <span className="w-7 shrink-0 text-center text-xs tabular-nums">{r.rank ?? '—'}</span>
            <span className="min-w-0 flex-1 truncate">{nameFor(r.sub, r.rank)}</span>
            <span className="shrink-0 tabular-nums">{formatNumero(Math.round(r.score), lingua)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="mt-10 flex flex-col items-center gap-3 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-bg-secondary text-fg-muted">
        <Trophy aria-hidden className="h-7 w-7" />
      </span>
      <p className="text-sm text-fg-muted">{text}</p>
    </div>
  );
}
